import {
  minValue,
  useGetIdentity,
  useGetOne,
  useRecordContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import { NumberInput } from "@/components/admin/number-input";

import type { OrgChartPerson, Sale } from "../types";

const requireWholeNumber = (value: unknown) =>
  value == null || Number.isInteger(value)
    ? undefined
    : "resources.sales.fields.accumulated_sales_invalid";

/**
 * "Ventas acumuladas a la fecha" plus the computed level, shown on the edit
 * form only (the RPC that stores the number needs an existing person). The
 * number is editable by direccion on someone else's row; everyone else, and
 * direccion on herself, sees it read-only. The lock is cosmetic: the
 * `set_accumulated_sales_at_onboarding` RPC and the DB guard stay the
 * authority. The level comes from the existing `org_chart_people` read and is
 * never editable here.
 */
export const SalesLevelInputs = () => {
  const record = useRecordContext<Sale>();
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const { data: person } = useGetOne<OrgChartPerson>(
    "org_chart_people",
    { id: record?.id as Identifier },
    { enabled: record?.id != null },
  );

  if (record?.id == null) {
    return null;
  }

  const canEditAccumulated =
    identity?.role === "direccion" && record.id !== identity.id;

  return (
    <>
      <NumberInput
        source="accumulated_sales_at_onboarding"
        label="resources.sales.fields.accumulated_sales"
        helperText="resources.sales.fields.accumulated_sales_helper"
        min={0}
        step={1}
        validate={[minValue(0), requireWholeNumber]}
        readOnly={!canEditAccumulated}
      />
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {translate("resources.sales.fields.level")}
        </p>
        <p className="text-sm text-muted-foreground">
          {person?.level_name ??
            translate("resources.sales.fields.level_empty")}
        </p>
      </div>
    </>
  );
};
